// The host seam — BOTH registries (contract 22 §4/§8, ledger §A11/§A14). Features register their
// settings surface INTO module 22; 22 imports none of them. `app/bootstrap.ts` hands these two
// functions to each registrant's `install*Settings(reg)`, so neither side knows the other exists.
//
// Module state, not a Pinia store (a deliberate read of contract §7): the child-route array IS the
// `/settings` parent's `children` — `routes.ts` binds it at module eval and `createRouter()` reads
// it by reference — so registrations must land before any store could exist. Same idiom as 05's
// `navContributions`/`navResetRegistry` and 07's `hydratorRegistry`. Nothing here persists and
// nothing resets on a context switch: entries are code, and their `visible()` predicates are
// reactive on their owners' state.
import { shallowReactive } from 'vue'
import type { RouteRecordRaw } from 'vue-router'

import type { SettingsNavEntry } from '../models/settingsNav.types'

import { useEnv } from '@/shared/config'

/** `shallowReactive` so `useSettingsNav()`'s computeds see an entry registered after first read. */
const navEntries = shallowReactive<SettingsNavEntry[]>([])

/** What registrants appended — tracked so `resetSettingsRegistry()` can undo registrations
 *  without dropping the host's own children out of the bound array. */
const registeredRoutes: RouteRecordRaw[] = []

/** The live `children` array of the `/settings` parent record (bound by `routes.ts`). */
let childRoutes: RouteRecordRaw[] = []

let sealed = false

/** Guarded: before bootstrap's `validateEnv()` there is no env, and "cannot tell" must fail loud. */
function isProduction(): boolean {
  try {
    return useEnv().MODE === 'production'
  } catch {
    return false
  }
}

/**
 * Register one settings-nav entry (contract §4). Idempotent by `key`: a duplicate throws outside
 * production so CI catches the composition bug, and outside CI replaces + warns rather than
 * white-screening the app over a nav item (§14).
 */
export function registerSettingsNavEntry(entry: SettingsNavEntry): void {
  const index = navEntries.findIndex((existing) => existing.key === entry.key)
  if (index === -1) {
    navEntries.push(entry)
    return
  }
  if (!isProduction()) {
    throw new Error(
      `[workspace-settings] duplicate settings nav entry key "${entry.key}" — keys must be unique across every registrant`,
    )
  }
  console.warn(
    `[workspace-settings] duplicate settings nav entry key "${entry.key}" — last registration wins`,
  )
  navEntries[index] = entry
}

/**
 * Append real child records to the `/settings` parent (contract §11, closes §18 Q3) — this is what
 * lets a pane owned by 30 or 23 render inside `SettingsLayout` with zero imports in either
 * direction. Must run BEFORE `createRouter()`: a record added afterwards would never resolve, so
 * once the registry is sealed this throws loudly instead of yielding a blank page (§14).
 */
export function registerSettingsChildRoutes(records: RouteRecordRaw[]): void {
  if (sealed) {
    const names = records.map((record) => String(record.name ?? record.path)).join(', ')
    throw new Error(
      `[workspace-settings] registerSettingsChildRoutes() ran after the router was created; ` +
        `route(s) [${names}] would never resolve. Install settings registrants before createAppRouter().`,
    )
  }
  childRoutes.push(...records)
  registeredRoutes.push(...records)
}

/**
 * Seal the child-route registry. `app/router/createAppRouter.ts` calls this immediately before
 * `createRouter()` — the one real footgun of this seam, made structural rather than documented.
 */
export function sealSettingsChildRoutes(): void {
  sealed = true
}

/** Bind the `/settings` parent's `children` array (called once by `routes.ts`, at module eval). */
export function bindSettingsChildRoutes(children: RouteRecordRaw[]): RouteRecordRaw[] {
  childRoutes = children
  return children
}

/** Read-only view of the registry for `useSettingsNav()`. */
export function settingsNavEntries(): readonly SettingsNavEntry[] {
  return navEntries
}

/** Test isolation: undo every REGISTRATION (the host's own children survive) and unseal. */
export function resetSettingsRegistry(): void {
  navEntries.length = 0
  for (const record of registeredRoutes) {
    const index = childRoutes.indexOf(record)
    if (index !== -1) childRoutes.splice(index, 1)
  }
  registeredRoutes.length = 0
  sealed = false
}
